import { useCallback, useEffect, useRef, useState } from "react";
import type { publicOperatorAdmin } from "@kintone-storage/openapi-types";
import { apiClient, ApiError, apiErrorMessage } from "@/api/client";

export type AutomaticAlignmentState = publicOperatorAdmin.components["schemas"]["AutomaticAttachmentAlignmentState"];
export type AutomaticAlignmentCommand = publicOperatorAdmin.components["schemas"]["AutomaticAttachmentAlignmentCommand"];
export type AutomaticAlignmentBlocker = publicOperatorAdmin.components["schemas"]["AutomaticAlignmentBlocker"];
export type AutomaticAlignmentController = ReturnType<typeof useAutomaticAttachmentAlignment>;

/** One controller per settings page. Its reads never hydrate the settings form. */
export function useAutomaticAttachmentAlignment(kintoneAppId: string, appBindingId: string) {
  const resource = kintoneAppId && appBindingId ? `${kintoneAppId}:${appBindingId}` : "";
  const currentResource = useRef(resource);
  currentResource.current = resource;
  const sequence = useRef(0);
  const inFlightRead = useRef<{ resource: string; sequence: number } | null>(null);
  const inFlightCommand = useRef<{ resource: string; sequence: number } | null>(null);
  const lastState = useRef<{ resource: string; value: AutomaticAlignmentState } | null>(null);
  const failures = useRef(0);
  const retryAllowed = useRef(true);
  const [snapshot, setSnapshot] = useState<{ resource: string; value: AutomaticAlignmentState; observedAt: number } | null>(null);
  const [readStatus, setReadStatus] = useState({ resource, isLoading: Boolean(resource), error: null as string | null });
  const [commandStatus, setCommandStatus] = useState({ resource, isRunning: false, error: null as string | null });
  const [readRevision, setReadRevision] = useState(0);
  const path = `/kintone-apps/${encodeURIComponent(kintoneAppId)}/automatic-attachment-alignment`;
  const accept = useCallback((result: AutomaticAlignmentState, observedAt: number) => {
    if (result.appBindingId !== appBindingId || result.kintoneAppId !== kintoneAppId) {
      throw new Error("対象アプリの自動整合化状態を確認できません。状態を再取得してください。");
    }
    const updated = { resource, value: result, observedAt };
    lastState.current = updated;
    setSnapshot(updated);
    setReadStatus({ resource, isLoading: false, error: null });
    failures.current = 0;
    retryAllowed.current = true;
  }, [appBindingId, kintoneAppId, resource]);

  const refresh = useCallback(async () => {
    if (!resource || currentResource.current !== resource || inFlightRead.current?.resource === resource || inFlightCommand.current?.resource === resource) return;
    const request = { resource, sequence: ++sequence.current, observedAt: performance.now() };
    inFlightRead.current = request;
    setReadStatus(previous => ({ resource, isLoading: lastState.current?.resource !== resource, error: previous.resource === resource ? previous.error : null }));
    try {
      const result = await apiClient<AutomaticAlignmentState>(path);
      if (currentResource.current !== resource || sequence.current !== request.sequence) return;
      accept(result, request.observedAt);
    } catch (error) {
      if (currentResource.current !== resource || sequence.current !== request.sequence) return;
      setReadStatus({ resource, isLoading: false, error: apiErrorMessage(error, "自動整合化の状態を取得できませんでした。") });
      failures.current++;
      retryAllowed.current = !(error instanceof ApiError) || [0, 408, 429, 500, 502, 503, 504].includes(error.status);
    } finally {
      if (inFlightRead.current === request) inFlightRead.current = null;
      if (currentResource.current === resource && sequence.current === request.sequence) setReadRevision(value => value + 1);
    }
  }, [resource, path, accept]);

  const runCommand = useCallback(async (payload: AutomaticAlignmentCommand): Promise<boolean> => {
    const previous = lastState.current;
    if (!resource || currentResource.current !== resource || previous?.resource !== resource || inFlightCommand.current?.resource === resource) return false;
    const request = { resource, sequence: ++sequence.current, observedAt: performance.now() };
    inFlightCommand.current = request;
    setCommandStatus({ resource, isRunning: true, error: null });
    let failed = false;
    try {
      const result = await apiClient<AutomaticAlignmentState>(path, { method: "POST", body: JSON.stringify(payload) });
      if (currentResource.current !== resource || sequence.current !== request.sequence) return false;
      accept(result, request.observedAt);
    } catch (error) {
      if (currentResource.current !== resource || sequence.current !== request.sequence) return false;
      failed = true;
      setCommandStatus({ resource, isRunning: true, error: apiErrorMessage(error, "操作の結果を確認できません。最新状態を確認してください。") });
      // A failed command may have committed. The retained display is not admission evidence.
      setReadStatus({ resource, isLoading: false, error: "操作後の最新状態を確認しています。確認できるまで新規実行は開始できません。" });
    } finally {
      if (inFlightCommand.current === request) inFlightCommand.current = null;
      if (currentResource.current === resource && sequence.current === request.sequence) {
        setCommandStatus(previousStatus => ({ ...previousStatus, isRunning: false }));
        // An older GET cannot overwrite the command result or block its fresh read.
        inFlightRead.current = null;
        if (failed) await refresh();
        else setReadRevision(value => value + 1);
      }
    }
    return !failed;
  }, [resource, path, accept, refresh]);
  const command = useCallback(async (action: "enable" | "stop" | "reconfirm", externalizationConfirmed?: boolean, confirmedRevision?: string) => {
    const previous = lastState.current;
    if (previous?.resource !== resource) return;
    if (confirmedRevision !== undefined && previous.value.revision !== confirmedRevision) { await refresh(); return; }
    await runCommand({ action, expectedRevision: previous.value.revision, ...(externalizationConfirmed ? { externalizationConfirmed: true } : {}) });
  }, [resource, runCommand, refresh]);
  const saveOptions = useCallback((options: NonNullable<AutomaticAlignmentState["options"]>, expectedRevision: string) =>
    runCommand({ action: "save_options", expectedRevision, options }), [runCommand]);

  useEffect(() => {
    sequence.current++;
    lastState.current = null;
    inFlightRead.current = null;
    inFlightCommand.current = null;
    failures.current = 0;
    retryAllowed.current = true;
    setSnapshot(null);
    setReadStatus({ resource, isLoading: Boolean(resource), error: null });
    setCommandStatus({ resource, isRunning: false, error: null });
    void refresh();
    return () => { sequence.current++; };
  }, [resource, refresh]);
  const state = snapshot?.resource === resource ? snapshot.value : null;
  const isLoading = readStatus.resource !== resource ? Boolean(resource) : readStatus.isLoading;
  const error = readStatus.resource === resource ? readStatus.error : null;
  const isCommandRunning = commandStatus.resource === resource && commandStatus.isRunning;
  const commandError = commandStatus.resource === resource ? commandStatus.error : null;
  useEffect(() => {
    if (!resource || isLoading || isCommandRunning || !retryAllowed.current) return;
    const base = state?.activeExecution || state?.state === "stop_requested" ? 3000 : 30_000;
    const timer = setTimeout(() => void refresh(), Math.min(30_000, base * 2 ** Math.min(failures.current, 4)));
    return () => clearTimeout(timer);
  }, [resource, state, isLoading, isCommandRunning, readRevision, refresh]);
  const settingsSaveBlockers: AutomaticAlignmentBlocker[] = state ? [...state.settingsSaveBlockers] : [];
  const canSaveSettings = Boolean(state && !isLoading && !error && !isCommandRunning && settingsSaveBlockers.length === 0);
  const canStartManual = Boolean(state && !isLoading && !error && !isCommandRunning && state.manualExecutionBlockers.length === 0);
  return { state, isLoading, error, isCommandRunning, commandError, canStartManual, canSaveSettings, settingsSaveBlockers, refresh, command, saveOptions };
}
